import pl from 'pluralize';
import { MigrateRequest, WebhooksResult, WebhookSubscription } from '~/models';
import MigratortronBaseService from './MigratortronBaseService';
import { logError, Logger } from '~/util/logger';
import { dedupeInner } from '~/util/array';
import {
  logMigrateResultsTable,
  logWebhooksPreviewTable,
} from '~/transformations/logging';
import ContensisRepository from '~/repositories/ContensisRepository';

export type MigrateWebhook = {
  diff?: string;
  error?: AppError;
  existing?: WebhookSubscription;
  id: string;
  originalId: string;
  source: WebhookSubscription;
  status: string;
  webhook: WebhookSubscription;
};

export type GetWebhooksArgs = {
  name?: string;
  ids?: string[];
  enabled?: boolean;
};

class WebhookMigrationService extends MigratortronBaseService {
  created = [] as string[];
  updated = [] as string[];
  deleted = [] as string[];

  constructor(payload: MigrateRequest, preview: boolean) {
    super(payload, preview);
  }

  MigrateWebhooks = async (getBy?: GetWebhooksArgs) => {
    const { isPreview } = this;

    // // Loads content types and components into memory
    // if (!this.isHydrated) await this.HydrateContensisRepositories();

    // Loads all webhooks from source and target repos into memory
    await this.GetWebhooksToMigrate(getBy);

    // Build and store the target webhooks
    // against each target.webhooks.migrate
    await this.BuildTargetWebhooks();

    const previewResult = this.mapMigrateResult();
    if (Logger.shouldLog('warning')) {
      logWebhooksPreviewTable(previewResult, Object.keys(this.targets));
    }

    if (!isPreview) {
      await this.CommitWebhooks();
    }

    let result = previewResult;
    if (!isPreview) {
      // mapMigrateResult can be slow to complete with payloads > 10k
      result = this.mapMigrateResult();
    }

    logMigrateResultsTable(result, 'webhooks');

    Logger.success(
      `Webhooks ${this.opName} ${isPreview ? `preview ready` : 'complete'}`,
      true
    );

    return result;
  };

  /** Loads all Webhooks from source and target repos into memory */
  GetWebhooksToMigrate = async (getBy: GetWebhooksArgs = {}) => {
    const { source, targets } = this;

    const hasWebhooksInPayload = !!source.webhooks.subscriptions?.length;

    // Webhooks are not supplied in the payload
    if (!hasWebhooksInPayload) {
      // Get entries from source repo
      await source.webhooks.GetSubscriptions({
        name: getBy.name || source.context.payload.query?.searchTerm,
        ids: getBy.ids || source.context.payload.query?.includeIds,
        enabled: getBy.enabled,
      });
    }

    if (source.webhooks.subscriptions?.length)
      for (const [, target] of Object.entries(targets)) {
        void this.queue.add(() => target.webhooks.GetSubscriptions());
      }

    // Wait until we've got all repo webhooks
    await this.queue.onIdle();
  };

  BuildTargetWebhooks = async () => {
    // Build webhooks for each target repo
    for (const [, target] of Object.entries(this.targets)) {
      await target.webhooks.BuildMigrateWebhooks({
        outputProgress: this.payload.outputProgress,
        ignoreErrors: this.ignoreErrors,
        onError: (b) => {
          this.AddError(b.sourceEntry.sys.id || '', b.error);
        },
      });
    }
  };

  CommitWebhooks = async () => {
    for (const [, target] of Object.entries(this.targets)) {
      await this.QueueWebhooks(target);
    }
  };

  QueueWebhooks = async (target: ContensisRepository) => {
    const migrateWebhooks = target.webhooks.migrate.filter((mw) =>
      ['create', 'update'].includes(mw.status)
    );
    const totalWebhooks = migrateWebhooks.length;

    if (totalWebhooks)
      Logger.info(
        `[webhooks] Committing ${totalWebhooks} ${pl('webhook', totalWebhooks)} in ${Logger.highlightText(
          target.alias
        )} ${Logger.standardText(target.project.id)}`,
        true
      );

    let currentIndex = 0;

    for (const migrateTag of migrateWebhooks) {
      // use queue so we can load tags in parallel
      void this.queue.add(async () => {
        Logger.info(
          `[webhooks] ${++currentIndex}/${totalWebhooks} [${
            migrateTag.status
          }] '${migrateTag.source.value}' | ${migrateTag.source.id}`
        );
        await this.CommitWebhook(migrateTag, target);
      });
    }

    await this.queue.onIdle();
  };

  CommitWebhook = async (
    migrateWebhook: MigrateWebhook,
    repo: ContensisRepository
  ): Promise<[AppError | null, WebhookSubscription | undefined]> => {
    if (!migrateWebhook.error) {
      const sourceWebhook = migrateWebhook.source;

      const upsertWebhook = migrateWebhook.webhook;

      // Upsert webhook
      const [error, newWebhook] = await repo.repo.UpdateOrCreateWebhook(
        migrateWebhook.existing,
        upsertWebhook
      );

      if (!error && newWebhook) {
        if (migrateWebhook.status === 'create') {
          this.created.push(sourceWebhook.id);
        } else if (migrateWebhook.status === 'update') {
          this.updated.push(sourceWebhook.id);
        }
      }

      if (error) {
        const mappedError = this.AddError(sourceWebhook.id, error);
        migrateWebhook.error = mappedError;
        logError(
          error.data || error,
          `${mappedError.message} | ${upsertWebhook.id}`
        );
      }
      return [error, newWebhook];
    }
    return [migrateWebhook.error, undefined];
  };

  mapMigrateResult = () => {
    const { sourceRepo, targetRepos } = this;
    const result = {
      webhooks:
        sourceRepo.projectId && sourceRepo.projectId !== 'null'
          ? { [sourceRepo.projectId]: { totalCount: 0 } }
          : undefined,
      errors: Object.keys(this.errors).length
        ? dedupeInner(Object.values(this.errors).flat())
        : undefined,
      webhooksResult: !this.isPreview
        ? {
            created: this.created.length,
            updated: this.updated.length,
            deleted: this.deleted.length,
            errors: Object.values(this.errors).flat().length || undefined,
          }
        : undefined,
      existing: Object.fromEntries(
        Object.keys(targetRepos).map((projectId) => [
          projectId,
          { totalCount: 0 },
        ])
      ),
      webhooksToMigrate: Object.fromEntries(
        Object.keys(targetRepos).map((projectId) => [projectId, {}])
      ),
    } as WebhooksResult;

    for (const [projectId, target] of Object.entries(targetRepos)) {
      result.existing[projectId] = {
        totalCount: target.webhooks.totalCount,
        webhooks: target.webhooks.migrate.map((w) => w.webhook),
      };

      const statuses = [
        ...new Set(target.webhooks.migrate.map((mn) => mn.status)),
      ];
      result.webhooksToMigrate.webhookIds = Object.fromEntries(
        target.webhooks.migrate.map((mw, idx) => [
          mw.originalId || mw.id || `[${idx}]`,
          {
            id: mw.id,
            name: mw.webhook.name,
            description: mw.webhook.description,
            enabled: mw.webhook.enabled,
            method: mw.webhook.method,
            url: mw.webhook.url,
            diff: mw.status === 'update' ? mw.diff : undefined,
            status: mw.status,
            error: mw.error,
          },
        ])
      );
      result.webhooksToMigrate[projectId] = Object.fromEntries([
        ...statuses.map((status) => [
          status,
          target.webhooks.migrate.filter((mn) => mn.status === status).length,
        ]),
        [
          'totalCount',
          target.webhooks.migrate?.filter((e) =>
            ['create', 'update', 'delete'].includes(e.status)
          )?.length || 0,
        ],
      ]);
    }
    if (result.webhooks) {
      result.webhooks[sourceRepo.projectId] = {
        totalCount: sourceRepo.webhooks.totalCount,
        // paths: this.MapNodesForResponse(sourceRepo.nodes.raw).map((n) => n.path),
      };
    }

    return result;
  };
}

export default WebhookMigrationService;
